import {
    Autocomplete,
    AutocompleteRenderInputParams,
    Box,
    Button,
    Checkbox,
    TextField,
    Typography
} from '@mui/material';
import { Option } from 'lib/types';
import React from 'react';
import { Row } from 'ui/Flex';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

interface AudienceAnalyticsAudiencePickerProps {
    onClose: () => void;
    options: Option[];
    onSubmit: (values: string[]) => void;
    selectedAudiences?: string[];
}

export const AudienceAnalyticsAudiencePicker: React.FC<AudienceAnalyticsAudiencePickerProps> = ({
    onClose,
    options,
    onSubmit,
    selectedAudiences
}) => {
    const [audienceSelectValue, setAudienceSelectValue] = React.useState<Option[]>(() => {
        if (selectedAudiences && selectedAudiences.length > 0) {
            return options.filter(option => selectedAudiences.includes(option.value));
        }
        return [];
    });
    const handleAudienceSelectChange = React.useCallback((_: never, newValue: Option[]) => {
        setAudienceSelectValue(newValue);
    }, []);
    const handleSubmit = React.useCallback(() => {
        onSubmit(audienceSelectValue.map(audience => audience.value));
    }, [audienceSelectValue, onSubmit]);
    const renderAudienceOption = React.useCallback((props, option, { selected }) => {
        const { key, ...optionProps } = props;
        return (
            <li key={key} {...optionProps}>
                <Checkbox icon={icon} checkedIcon={checkedIcon} sx={{ mr: 1 }} checked={selected} />
                {option.label}
            </li>
        );
    }, []);
    const renderInput = React.useCallback(
        (params: AutocompleteRenderInputParams) => (
            <TextField
                {...params}
                sx={{ mb: 2 }}
                variant="outlined"
                autoFocus
                placeholder="Start typing to search audiences"
            />
        ),
        []
    );

    const getOptionDisabled = React.useCallback(
        (option: Option) => {
            if (
                audienceSelectValue.some(audience => audience.value === option.value) ||
                audienceSelectValue.length < 4
            ) {
                return false;
            }
            return true;
        },
        [audienceSelectValue]
    );
    return (
        <Box>
            <Typography variant="h6" gutterBottom>
                Select audiences to compare
            </Typography>
            <Autocomplete
                multiple
                options={options}
                value={audienceSelectValue}
                onChange={handleAudienceSelectChange}
                disableCloseOnSelect
                renderOption={renderAudienceOption}
                style={{ width: 500 }}
                renderInput={renderInput}
                getOptionDisabled={getOptionDisabled}
            />
            <Row flex={1} align="flex-end" gutter>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="contained" color="primary" onClick={handleSubmit}>
                    Submit
                </Button>
            </Row>
        </Box>
    );
};
